import { getConfig } from '@superbet-group/web.lib.config';
import {
    createCTRestClient,
    RestClient,
} from '@superbet-group/web.lib.rest';
import { IResponse } from '@superbet-group/web.lib.utilities';
import { getClientSourceParam } from '@superbet-group/web.lib.vue-utilities';

import { transformPromotion } from '@app/promotionHub/promotions/mappers/transformPromotion';
import { ComtradePromotionDto } from '@src/app/promotionHub/promotions/types/dtos/ComtradePromotionDto';
import { defaultHttpTimeoutMs } from '@core/constants';
import { getCsrfToken } from '@core/services/authentication/helpers';
import { PromotionResponse } from '../types/dtos/PromotionResponse';
import BonusProvider from '@core/models/bonus/BonusProvider';
import { BetlerPromotionDto } from '../types/dtos/BetlerPromotionDto';
import { PromotionDto } from '../types/dtos/PromotionDto';
import { Promotion } from '../types/Promotion';

const endpoints = getConfig().services.playerAccountManagement;
const restClient: RestClient = createCTRestClient(
    getConfig().promotion!.host,
    defaultHttpTimeoutMs,
    getClientSourceParam(),
    true,
    getCsrfToken,
);
const isBetlerProvider = getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Betler;

export async function getPromotions(): Promise<Promotion[]> {
    const response = await restClient.get<IResponse<PromotionResponse>>(endpoints.getListOfPromotions!);
    return response.data.Promotions.map((promotion: ComtradePromotionDto) => transformPromotion[getConfig().countrySpecific.country](promotion)).filter((promotion) => !!promotion) as Promotion[];
}

export async function getPlayerPromotions(): Promise<Promotion[]> {
    const response = await restClient.get<IResponse<PromotionResponse | BetlerPromotionDto[]>>(
        endpoints.getListOfPlayerPromotions!,
        {
            params: {
                lang: getConfig().appLocale.locale,
            },
        });
    const promotions = isBetlerProvider
        ? response.data as BetlerPromotionDto[]
        : (response.data as PromotionResponse).Promotions;

    return promotions.map((promotion: PromotionDto) => transformPromotion[getConfig().countrySpecific.country](promotion))
        .filter((promotion) => !!promotion) as Promotion[];
}

export async function promotionOptIn(code: string): Promise<Promotion[]> {
    const payload: Record<string, string> = isBetlerProvider
        ? {
            lang: getConfig().appLocale.locale,
            action: 'opt_in',
            customer_campaign_id: code,
        }
        : { promotionOptInCode: code };
    const response = await restClient.post<Record<string, string>, IResponse<PromotionResponse | BetlerPromotionDto>>(
        endpoints.promotionOptIn!,
        payload,
    );
    let promotions = [];
    if (isBetlerProvider) {
        promotions = [transformPromotion[getConfig().countrySpecific.country](response.data)];
    } else {
        promotions = (response.data as PromotionResponse).Promotions
            .map((promotion: ComtradePromotionDto) => transformPromotion[getConfig().countrySpecific.country](promotion));
    }

    return promotions.filter((promotion) => !!promotion) as Promotion[];
}
